"use client";

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { getAssistantOptions, updateAssistantOptions } from "@/lib/storage";

// Post-refactor (2026-05-19, ADR-041): replaces the previous user-editable domain list
// (`addDomain` / `removeDomain` / `strict`) with a single "limit to recommended sources"
// tickbox. The recommended-sources list is currently empty — it will be populated by a
// future tenant-administrator panel (see ADR-041). When the tickbox is ON and the list
// is empty, the frontend sends `webSearchAllowedDomains: []` so the backend renders a
// no-op web search (visual signal on the chip indicates the gated state).

type WebSearchDomainsContextValue = {
  limitToRecommended: boolean;
  setLimitToRecommended: (value: boolean) => void;
  recommendedSources: string[];
  setThreadId: (threadId: string | undefined) => void;
};

const WebSearchDomainsContext = createContext<WebSearchDomainsContextValue>({
  limitToRecommended: false,
  setLimitToRecommended: () => {},
  recommendedSources: [],
  setThreadId: () => {},
});

export function WebSearchDomainsProvider({ children }: { children: ReactNode }) {
  const [limitToRecommended, setLimitToRecommendedState] = useState<boolean>(false);
  // TODO(ADR-041): fetch tenant-configured allowlist from /api/admin/tenant-settings
  // once the admin panel ships. Empty list today.
  const [recommendedSources] = useState<string[]>([]);
  const threadIdRef = useRef<string | undefined>(undefined);

  const setThreadId = useCallback((id: string | undefined) => {
    threadIdRef.current = id;
    const stored = getAssistantOptions(id);
    setLimitToRecommendedState(stored.webSearchLimitToRecommended);
  }, []);

  const setLimitToRecommended = useCallback((value: boolean) => {
    setLimitToRecommendedState(value);
    updateAssistantOptions(threadIdRef.current, { webSearchLimitToRecommended: value });
  }, []);

  return (
    <WebSearchDomainsContext.Provider
      value={{ limitToRecommended, setLimitToRecommended, recommendedSources, setThreadId }}
    >
      {children}
    </WebSearchDomainsContext.Provider>
  );
}

export function useWebSearchDomains() {
  return useContext(WebSearchDomainsContext);
}
